import { AfterViewInit, Component, EventEmitter, OnInit } from '@angular/core';
import { IDateRangePickerDashboardItem } from 'src/app/components/date-picker/date-picker.component';
import { IMultipleSelectDashboardItem } from 'src/app/components/multiple-select/multiple-select.component';
import { TableDashboardItemHelper } from 'src/app/components/table-dashboard-item/table-dashboard-item-helper';
import { TableColumnItem } from 'src/app/components/table-dashboard-item/table-dashboard-item.component';
import { IDashboardItemDataLoaderLink } from 'src/app/services/dashboard-item/IDashboardItemDataLoaderLink';
import { DashboardService } from 'src/app/services/dashboard/dashboard.service';
import { IDataLoaderResult } from 'src/app/services/data-loader/data-loader-result';
import { DataLoaderFactories } from 'src/app/services/data-loader/data-loader.service';
import {
    IQueryContext,
    QueryContextDefaultSettings,
    QueryContextSettingID,
} from 'src/app/services/query-context/models/IQueryContext';
import { QueryContextService } from 'src/app/services/query-context/query-context.service';
import {
    DatasourceColumnId,
    datasourceColumnOrderByQuery,
} from 'src/app/utils/datasource-sql-map/datasource';
import { EChartsMetric } from 'src/app/utils/echarts/echarts-helper';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { currentDataProject } from 'src/app/services/data-project/models/data-project.model';
import { DashboardItemFactories } from '../common/items/common-dashboard-items';
import { FilterCategoryDashboardItemFactoryOptions } from '../common/items/filter/category-filter-dashboard-item/filter-category';
import { ModifyTimeDashboardItemFactory } from '../common/items/modify-time';
import { IMultiSelectSettingDashboardItem } from '../common/items/setting/multi-select/multi-select.component';
import { IToggleSettingDashboardItem } from '../common/items/setting/toggle/toggle.component';
import { StandardDashboardWorker } from '../common/standard-dashboard';
import { MopaCampaignsChartDocs } from './mopa-campaigns.chart-documentation';
import { CampaignsMastertableDataLoaderFactory } from './data-loaders/campaigns-mastertable';
import { CampaignTableDashoardItemHelper } from './items/campaigns-table';
import { BiVizDashboardCampaignsDatasource } from './sql-maps/biviz-dashboard-campaigns-sql-map';

@Component({
    selector: 'app-mopa-campaigns',
    templateUrl: './mopa-campaigns.component.html',
    styleUrls: ['./mopa-campaigns.component.scss'],
    providers: [DashboardService, QueryContextService],
})
export class MopaCampaignsComponent
    extends StandardDashboardWorker
    implements OnInit, AfterViewInit
{
    override DASHBOARD_NAME = 'Campaigns';
    override DASHBOARD_ID = 'mopa-campaigns';

    values = Object.values;

    private defaultGcpProjectId: GcpProjectId = currentDataProject().gcpProjectId;

    /////////////////////////////////////////////////////////////////
    ////
    ////    DATALOADERS
    ////    Some DataLoader can be used in multiple charts.
    ////    That's why there are created here and later referenced
    ////
    /////////////////////////////////////////////////////////////////
    // dashboard config:
    // active / all campaigns
    // pln usd net/gross
    // select columns
    // revenue type
    // first open date
    // app store,country, traffic source, campaign name, campaign_adgroup, campaign_creative, campaign_network
    override filterDataLoaders = {
        'mopa.campaigns.filter.firebase_app_store': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.firebase_app_store',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_APP_STORE',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        'mopa.campaigns.filter.firebase_country_tier': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.firebase_country_tier',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY_TIER',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        'mopa.campaigns.filter.firebase_country': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.firebase_country',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'FIREBASE_COUNTRY',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            filterContext: ['FIREBASE_COUNTRY_TIER'],
        }).create(),
        'mopa.campaigns.filter.attribution_traffic_source': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.campaigns.filter.attribution_traffic_source',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_TRAFFIC_SOURCE',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        'mopa.campaigns.filter.attribution_network_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.attribution_network_name',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_NETWORK_NAME',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        'mopa.campaigns.filter.attribution_campaign_name': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId:
                'mopa.campaigns.filter.attribution_campaign_name',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.campaigns.filter.attribution_ad': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.attribution_ad',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_AD',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            filterContext: true,
        }).create(),
        'mopa.campaigns.filter.attribution_ad_length': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.attribution_ad_length',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_AD_LENGTH_S',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            orderByQuery: datasourceColumnOrderByQuery(
                'ATTRIBUTION_AD_LENGTH_S'
            ),
        }).create(),
        'mopa.campaigns.filter.attribution_ad_size': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.attribution_ad_size',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_AD_SIZE',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        'mopa.campaigns.filter.attribution_ad_aspect_ratio':
            DataLoaderFactories['common.filter.datasource_column']({
                createdInstanceId:
                    'mopa.campaigns.filter.attribution_ad_aspect_ratio',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
                datasourceColumnId: 'ATTRIBUTION_AD_ASPECT_RATIO',
                datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
            }).create(),
        'mopa.campaigns.filter.attribution_ad_type': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.attribution_ad_type',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_AD_TYPE',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        'mopa.campaigns.filter.attribution_ad_theme': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.attribution_ad_theme',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_AD_THEME',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
        // 'ATTRIBUTION_AD_VERSION'
        'mopa.campaigns.filter.attribution_ad_version': DataLoaderFactories[
            'common.filter.datasource_column'
        ]({
            createdInstanceId: 'mopa.campaigns.filter.attribution_ad_version',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasourceColumnId: 'ATTRIBUTION_AD_VERSION',
            datasourceSqlMap: BiVizDashboardCampaignsDatasource.sqlMap,
        }).create(),
    };

    override chartDataLoaders = {
        'mopa.campaigns.modify_time_campaigns_datasource': DataLoaderFactories[
            'common.modify_time'
        ]({
            createdInstanceId:
                'mopa.campaigns.modify_time_campaigns_datasource',
            gcpProjectId: this.defaultGcpProjectId,
            queryCacheService: this.queryCache,
            datasetId: 'bi_viz',
            tableId: 'dashboard_campaigns',
        }).create(),
        'mopa.campaigns.mastertable': new CampaignsMastertableDataLoaderFactory(
            {
                createdInstanceId: 'mopa.campaigns.mastertable',
                gcpProjectId: this.defaultGcpProjectId,
                queryCacheService: this.queryCache,
            }
        ).create(),
    };

    /////////////////////////////////////////////////////////////////
    ////
    ////    [DASHBOARD ITEM <-> DATALOADER] LINKS
    ////
    /////////////////////////////////////////////////////////////////
    // settingItemDateAggregation: ISettingDashboardItem = {
    //     options: {
    //         id: QueryContextSettingID.DATE_AGGREGATION,
    //         viewtype: 'singleSelect',
    //         displayName: 'Date aggregation',
    //         value: 'day' as datePartDateTrunc,
    //         selectOptions: ['day', 'week', 'month', 'quarter', 'year'] as
    //             | 'day'
    //             | datePartDateTrunc[],
    //     },
    // };
    settingSelectedMetrics: IMultiSelectSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS,
            viewtype: 'multiSelect',
            displayName: 'Selected Metrics',
            value:
                this.getInitialContext().settings[
                    QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS
                ] ?? [],
            selectOptions:
                CampaignTableDashoardItemHelper.getAllDefaultMetrics().map(
                    (metric: TableColumnItem) => {
                        return { value: metric.id, label: metric.name };
                    }
                ),
        },
    };

    settingActiveAllCampaigns: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CAMPAIGN_ACTIVE_ALL,
            viewtype: 'toggle',
            value: 'all',
            selectOptions: ['all', 'active'],
        },
    };

    settingItemNetGross: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.NET_GROSS,
            viewtype: 'toggle',
            value: 'net',
            selectOptions: ['net', 'gross'],
        },
    };
    settingItemCurrency: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.CURRENCY,
            viewtype: 'toggle',
            value: 'pln',
            selectOptions: ['pln', 'usd'],
        },
    };
    settingRevenueType: IToggleSettingDashboardItem = {
        options: {
            id: QueryContextSettingID.REVENUE_TYPE,
            viewtype: 'toggle',
            value: 'total',
            selectOptions: ['total', 'inapp', 'ad'],
        },
    };

    // CHARTS
    modify_time_campaigns_datasource: IDashboardItemDataLoaderLink = {
        id: 'lnk.mopa.campaigns.modify_time_campaigns_datasource',
        // dashboardItemFactory: DashboardItemFactories['common.modify_time']({
        dashboardItemFactory: new ModifyTimeDashboardItemFactory({
            createdInstanceId:
                'mopa.campaigns.modify_time_campaigns_datasource',
            columnId: 'value',
        }),
        dataLoaderEmitter:
            this.chartDataLoaders[
                'mopa.campaigns.modify_time_campaigns_datasource'
            ].emitter,
    };

    mastertable = {
        id: 'lnk.mopa.campaigns.mastertable',
        dashboardItem: TableDashboardItemHelper.getTableDashboardItem({
            dashboardName: 'Campaign',
            instanceId: 'mopa.campaigns.mastertable',
            dashboardItem: {
                title: 'Campaigns mastertable',
                chartDocumentation: MopaCampaignsChartDocs.mastertable,
                viewMode: 'scroll',
            },
            columnsGetter: CampaignTableDashoardItemHelper.metricsGetter,
            splitTableByQueryContext: true,
        }),
        dataLoaderEmitter:
            this.chartDataLoaders['mopa.campaigns.mastertable'].emitter,
    };

    // FILTERS
    filterDashboardItemLinks: Partial<
        Record<DatasourceColumnId, IDashboardItemDataLoaderLink>
    > = {
        FIREBASE_FIRST_OPEN_DATE: {
            id: 'lnk.mopa.campaigns.filter.firebase_first_open_date',
            dashboardItemFactory: DashboardItemFactories['filter.daterange']({
                createdInstanceId:
                    'mopa.campaigns.filter.fFirebase_first_open_date',
                datasourceColumnId: 'FIREBASE_FIRST_OPEN_DATE',
                forcedSplitable: true,
                spltAggregates: [
                    'day',
                    'week',
                    'month',
                    'quarter',
                    'year',
                    'total',
                ],
            }),
            // empty emitter DataRangePicker doesn't need data
            dataLoaderEmitter: new EventEmitter<IDataLoaderResult>(),
        },
        FIREBASE_APP_STORE: {
            id: 'lnk.mopa.campaigns.filter.firebase_app_store',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.firebase_app_store',
                    'FIREBASE_APP_STORE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.firebase_app_store'
                ].emitter,
        },
        FIREBASE_COUNTRY_TIER: {
            id: 'lnk.mopa.campaigns.filter.firebase_country_tier',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.firebase_country_tier',
                    'FIREBASE_COUNTRY_TIER'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.firebase_country_tier'
                ].emitter,
        },
        FIREBASE_COUNTRY: {
            id: 'lnk.mopa.campaigns.filter.firebase_country',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.firebase_country',
                    'FIREBASE_COUNTRY'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.campaigns.filter.firebase_country']
                    .emitter,
        },
        ATTRIBUTION_TRAFFIC_SOURCE: {
            id: 'lnk.mopa.campaigns.filter.attribution_traffic_source',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_traffic_source',
                    'ATTRIBUTION_TRAFFIC_SOURCE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.attribution_traffic_source'
                ].emitter,
        },
        ATTRIBUTION_NETWORK_NAME: {
            id: 'lnk.mopa.campaigns.filter.attribution_network_name',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_network_name',
                    'ATTRIBUTION_NETWORK_NAME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.attribution_network_name'
                ].emitter,
        },
        ATTRIBUTION_CAMPAIGN_NAME: {
            id: 'lnk.mopa.campaigns.filter.attribution_campaign_name',
            dashboardItemFactory: DashboardItemFactories['filter.category']({
                createdInstanceId:
                    'mopa.campaigns.filter.attribution_campaign_name',
                datasourceColumnId: 'ATTRIBUTION_CAMPAIGN_NAME',
                showQuickSelectionButtons: true,
            }),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.attribution_campaign_name'
                ].emitter,
        },
        ATTRIBUTION_AD: {
            id: 'lnk.mopa.campaigns.filter.attribution_ad',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_ad',
                    'ATTRIBUTION_AD'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders['mopa.campaigns.filter.attribution_ad']
                    .emitter,
        },
        ATTRIBUTION_AD_LENGTH_S: {
            id: 'lnk.mopa.campaigns.filter.attribution_ad_length',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_ad_length_s',
                    'ATTRIBUTION_AD_LENGTH_S'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.attribution_ad_length'
                ].emitter,
        },
        ATTRIBUTION_AD_SIZE: {
            id: 'lnk.mopa.campaigns.filter.attribution_ad_size',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_ad_size',
                    'ATTRIBUTION_AD_SIZE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.attribution_ad_size'
                ].emitter,
        },
        ATTRIBUTION_AD_ASPECT_RATIO: {
            id: 'lnk.mopa.campaigns.filter.attribution_ad_aspect_ratio',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_ad_aspect_ratio',
                    'ATTRIBUTION_AD_ASPECT_RATIO'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.attribution_ad_aspect_ratio'
                ].emitter,
        },
        ATTRIBUTION_AD_TYPE: {
            id: 'lnk.mopa.campaigns.filter.attribution_ad_type',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_ad_type',
                    'ATTRIBUTION_AD_TYPE'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.attribution_ad_type'
                ].emitter,
        },
        ATTRIBUTION_AD_THEME: {
            id: 'lnk.mopa.campaigns.filter.attribution_ad_theme',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_ad_theme',
                    'ATTRIBUTION_AD_THEME'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.attribution_ad_theme'
                ].emitter,
        },
        ATTRIBUTION_AD_VERSION: {
            id: 'lnk.mopa.campaigns.filter.attribution_ad_version',
            dashboardItemFactory: DashboardItemFactories['filter.category'](
                new FilterCategoryDashboardItemFactoryOptions(
                    'mopa.campaigns.filter.attribution_ad_version',
                    'ATTRIBUTION_AD_VERSION'
                )
            ),
            dataLoaderEmitter:
                this.filterDataLoaders[
                    'mopa.campaigns.filter.attribution_ad_version'
                ].emitter,
        },
    };

    override ngOnInit() {
        super.ngOnInit();
        console.log({
            msg: 'MopaCampaignsComponent.ngOnInit',
            cancellationToken: this.cancellationToken,
        });
    }
    ngAfterViewInit() {
        console.log({
            msg: 'MopaCampaignsComponent.ngAfterViewInit',
            cancellationToken: this.cancellationToken,
        });

        this.standardDashboardInitialize();
    }

    override getInitialContext(): IQueryContext {
        const initialContext = QueryContextService.INITIAL_CONTEXT;

        const fodDateIC: IDateRangePickerDashboardItem['datasourceColumn'] = {
            id: 'FIREBASE_FIRST_OPEN_DATE',
            state: {
                value: {
                    preset: 'last 30 days',
                    splitAggregate: 'day',
                },
                isFilterApplied: true,
                isSplitApplied: true,
            },
        };

        const firebaseAppStoreIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'FIREBASE_APP_STORE',
                state: {
                    value: {
                        options: [
                            'Amazon Appstore',
                            'Google Play',
                            'Microsoft Store',
                            'iTunes',
                            'iTunes macOS',
                        ],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        const trafficSourceIC: IMultipleSelectDashboardItem['datasourceColumn'] =
            {
                id: 'ATTRIBUTION_TRAFFIC_SOURCE',
                state: {
                    value: {
                        options: ['Non-Organic'],
                        filterType: 'include',
                    },
                    isFilterApplied: true,
                    isSplitApplied: false,
                },
            };

        // set initialContext for AppStore to Google Play
        initialContext.datasourceColumns = [fodDateIC, trafficSourceIC];

        initialContext.settings[
            QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS
        ] =
            QueryContextDefaultSettings[
                QueryContextSettingID.CAMPAIGNS_SELECTED_METRICS
            ]!;
        initialContext.settings['revenueType'] = 'total';

        return initialContext;
    }

    /** Return metricsGetter with overriden context */
    static overrideContextInRevenueMetricsGetter(
        metricsGetter: (context: IQueryContext) => Record<string, EChartsMetric>
    ): (context: IQueryContext) => Record<string, any> {
        return (context: IQueryContext) => {
            // deepcopy
            const overridenContext: IQueryContext = JSON.parse(
                JSON.stringify(context)
            );

            overridenContext.settings['showTotalRevenue'] =
                context.settings['revenueType'] === 'total';
            overridenContext.settings['showInappRevenue'] =
                context.settings['revenueType'] === 'inapp';
            overridenContext.settings['showAdRevenue'] =
                context.settings['revenueType'] === 'ad';

            return metricsGetter(overridenContext);
        };
    }
}
